import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form, Button, FormGroup, Input } from 'reactstrap';
import { Link, useSearchParams } from 'react-router-dom'
import ActiveToggle from '../misc/ActiveToggle';
import { ScheduleSyncUsers } from '../../store/ScheduleSync/ScheduleSyncUserAction';
import { CacheRequestState } from '../../models/RequestState';
import { PageResult } from '../../models/CoreModels';
import StatusCode from '../../util/StatusCode';
import TableHorizontal from '../misc/TableHorizontal';
import * as Helper from '../../util/Helper';
import SelectInput from '../misc/SelectInput';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import RequestParser from '../../util/RequestParser';
import { useTranslation } from 'react-i18next';
import { ScheduleSync_CoreUserListing } from '../../models/CoreScheduleSyncModels';
import { InfoObject } from '../misc/TableHorizontalPagedToggle';
import { getErrorObject } from '../../models/ErrorObject';
import { UserScheduleSearchPageRequest } from '../../models/Requests';
import { RootState } from '../../store/store';



const UsersScheduleSync = () => {

    const { t } = useTranslation(['common', 'scheduleSync']);
    const dispatch = useDispatch();

    const [searchParams, setSearchParams] = useSearchParams();
    const [filterState, setFilterState] = useState<Partial<UserScheduleSearchPageRequest>>({});

    const users = useSelector<RootState, CacheRequestState<PageResult<ScheduleSync_CoreUserListing>>>(state => state.schedulesync_users.users);

    var filter = RequestParser.parseUserSchedulePageRequest(searchParams);

    useEffect(() => {
        ScheduleSyncUsers(filter)(dispatch);
    }, [dispatch]);

    useEffect(() => {
        ScheduleSyncUsers(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);


    const onSearch: React.FormEventHandler<HTMLFormElement | HTMLButtonElement> = (e) => {
        e.preventDefault();
        setSearchParams(getNewFilterState(filterState));
    }

    const clearFilter = () => {
        setSearchParams([]);
        setFilterState([]);
    }

    const getNewFilterState = (oldFilterState: Partial<UserScheduleSearchPageRequest>) => {
        let newFilterState: Partial<UserScheduleSearchPageRequest> = {};
        Object.entries(oldFilterState).forEach(x => {
            if (x[1] !== undefined) {
                newFilterState[x[0]] = x[1];
            }
        })
        return newFilterState;
    }

    const getRoleItems = () => {
        let roleTypes = {
            organizer: t('userSchedule.organizer', { ns: 'scheduleSync' }),
            participant: t('userSchedule.participant', { ns: 'scheduleSync' })
        };
        return Object.entries(roleTypes)
            .map(([key, value]) => ({
                value: key,
                text: t('filter.on', { ns: 'common' }) + ` ${value}`
            }))
    }

    const userRoles = (roleList: { [key: string]: boolean; }) => {
        let roleTypesList: string[] = [];
        Object.entries(roleList).map(([key, value]) => {
            if (value) {
                roleTypesList.push(t('userSchedule.' + key, { ns: 'scheduleSync' }))
            }
        })

        if (!roleTypesList.length) {
            return '-';
        }
        return roleTypesList.join(', ');
    }

    const renderFilter = () => {

        return (
            <Form autoComplete="off" onSubmit={onSearch}>
                <Row className="search-box">
                    <Col lg="6">
                        <FormGroup className="search-form">
                            <Input type="text" name="search" value={filterState.search || ''}
                                onChange={(e) => setFilterState({ ...filterState, search: e.target.value, page: 1 })}
                                placeholder={t('userSchedule.searchUser', { ns: 'scheduleSync' })} />
                        </FormGroup>
                        <FormGroup className="search-form">
                            <Input type="text" name="socialnumber" value={filterState.socialnumber || ''}
                                onChange={(e) => setFilterState({ ...filterState, socialnumber: e.target.value, page: 1 })}
                                placeholder={t('userSchedule.searchCivic', { ns: 'scheduleSync' })} />
                        </FormGroup>
                    </Col>

                    <Col lg="3">
                        <SelectInput
                            items={getRoleItems()}
                            onChange={(name, value) => setFilterState({ ...filterState, role: value, page: 1 })}
                            name='role'
                            selected={filterState.role}
                            defaultText={t('userSchedule.showAllRoles', { ns: 'scheduleSync' })}
                        />
                    </Col>

                    <Col lg="3" className="flex dir-column-lg space-between">
                        <ActiveToggle
                            onlyActive={filterState.onlyActive ?? true}
                            update={(active) => setFilterState({ ...filterState, onlyActive: active, page: 1 })}
                            disabled={users.code === StatusCode.PENDING}
                        />
                        <div className="button-box">
                            <Button color="primary" onSubmit={onSearch}>{t('action.search', { ns: 'common' })}</Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        )
    }

    const mapUsers = (): InfoObject[] => {
        if ((users.code === StatusCode.COMPLETE) || users.code === StatusCode.PENDING) {
            if (users.data === undefined) {
                return []
            }
            return users.data.values.map(u => {
                return ({
                    id: u.userId,
                    data: [
                        { value: <Link to={`/schedulesync/user/${u.userId}`}>{u.displayName}</Link> },
                        { value: u.emailAddress },
                        { value: userRoles({ organizer: u.hasOrganizerRole, participant: u.hasParticipantRole }) },
                        { value: Helper.active(u.active) },
                    ]
                })
            });
        } else {
            return [];
        }
    }

    const renderBody = () => {
        const usersInfo = mapUsers();

        return (
            <TableHorizontal
                header={[
                    { value: t('genericTableHeaders.name', { ns: 'common' }) },
                    { value: t('genericTableHeaders.email', { ns: 'common' }) },
                    { value: t('genericTableHeaders.role', { ns: 'common' }) },
                    { value: t('genericTableHeaders.active', { ns: 'common' }) }
                ]}
                info={usersInfo}
                error={users.code === StatusCode.ERROR}
                errorMsg={users.code === StatusCode.ERROR ? getErrorObject(users).description : undefined}
                searching={users.code === StatusCode.PENDING}
                noResMsg={t('userSchedule.noUsersFound', { ns: 'scheduleSync' })}
            />
        )
    }

    return (
        <div className="container users_schedulesync">
            <div className="header--fixed large-filter">
                <h1>{t('userSchedule.title', { ns: 'scheduleSync' })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderBody()}
            </div>

            <FilterBottomComponent
                requestState={users}
                clearFilter={clearFilter}
                updateFilter={(value) => setSearchParams(getNewFilterState({ ...filterState, page: value }))}
            />
        </div>
    )
}

export default UsersScheduleSync;
